import { useState } from "react"
import FormErrors from "components/formParts/formErrors"
import { useTranslation } from "react-i18next"
import { ifPasswordIsSecure } from "utils/validation"
import PasswordInput from "components/inputs/passwordInput"
import Button from "components/inputs/button"
import ButtonGroup from "components/buttons/buttonGroup"
import ProcessingButton from "components/inputs/processingButton"
import useLayout from "hooks/useLayout"
import useAuth from "hooks/useAuth"
import Form from "components/forms/form"
import { ReactComponent as SecurityIcon } from "assets/icons/security.svg"
import Section from "@/components/layout/section"
import SectionHeader from "@/components/layout/sectionHeader"
import SectionHeaderTitle from "@/components/layout/sectionHeaderTitle"
import { decodeFirebaseError } from "@/utils/firebaseHelpers"

const ChangePasswordForm = () => {

    const { t } = useTranslation()

    // Form data.
    const [currentPassword, setCurrentPassword] = useState('')
    const [newPassword, setNewPassword] = useState('')
    const [confirmPassword, setConfirmPassword] = useState('')

    // Form status.
    const [errors, setErrors] = useState<string[]>([])
    const [success, setSuccess] = useState(false)

    // Auth hooks.
    const { changePassword, isChangingPassword } = useAuth()

    // Layout hooks.
    const { addToast } = useLayout()

    // Validate form.
    const validate = () => {
        const newErrors: string[] = []

        if (!currentPassword) {
            newErrors.push(t('currentPasswordRequired'))
        }

        if (!newPassword) {
            newErrors.push(t('newPasswordRequired'))
        }

        if (newPassword !== confirmPassword) {
            newErrors.push(t('passwordMismatch'))
        }

        if (!ifPasswordIsSecure(newPassword)) {
            newErrors.push(t('weakPassword'))
        }

        if (newErrors.length > 0) {
            setErrors(newErrors)
            return false
        }

        setErrors([])
        return true
    }

    // Handle submit.
    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault()
        if (!validate()) return
        setSuccess(false)
        setErrors([])
        
        try {
            await changePassword({ currentPassword, newPassword });
            setSuccess(true)
            setCurrentPassword('')
            setNewPassword('')
            setConfirmPassword('')
            addToast({ text: t('toastPasswordChangeSuccess'), colorScheme: 'green' })
        } catch (error: any) {
            const errorMessage = decodeFirebaseError(error?.code || error?.message || 'Unknown error')
            setErrors([errorMessage])
            addToast({ text: t('toastPasswordChangeError'), colorScheme: 'red' })
        }
    }

    // Handle current password change.
    const handleCurrentPasswordChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setCurrentPassword(e.target.value)
    }

    // Handle new password change.
    const handleNewPasswordChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setNewPassword(e.target.value)
    }

    // Handle confirm password change.
    const handleConfirmPasswordChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setConfirmPassword(e.target.value)
    }

    // Return the form.
    return (
        <>
            <SectionHeader>
                <SectionHeaderTitle>
                    <h2>{t('titlePassword')}</h2>
                </SectionHeaderTitle>
            </SectionHeader>
            
            <Form
                onSubmit={handleSubmit}
                className="group"
                isLoading={isChangingPassword}
            >
                <Section>
                    <PasswordInput
                        label={t('currentPassword')}
                        value={currentPassword}
                        onChange={handleCurrentPasswordChange}
                        autoComplete="current-password"
                        required
                    />
                    
                    <PasswordInput
                        label={t('newPassword')}
                        value={newPassword}
                        onChange={handleNewPasswordChange}
                        autoComplete="new-password"
                        required
                    />
                    
                    <PasswordInput
                        label={t('confirmNewPassword')}
                        value={confirmPassword}
                        onChange={handleConfirmPasswordChange}
                        autoComplete="new-password"
                        required
                    />
                    <ButtonGroup>
                        {isChangingPassword ? (
                            <ProcessingButton design="primary" />
                        ) : (
                            <Button wideOnMobile 
                                type="submit" 
                                design="primary"
                                disabled={!currentPassword || !newPassword || !confirmPassword}
                            >
                                <SecurityIcon />
                                <span>{t('buttonChangePassword')}</span>
                            </Button>
                        )}
                    </ButtonGroup>
                </Section>
                
                {errors.length > 0 && <FormErrors errors={errors} />}
            </Form>
        </>
    )
}

export default ChangePasswordForm
